import React, { useState } from "react";
import classNames from "classnames";
import { Button } from "../../../general/Button/Button";
import { ArrowCornerIcon, TriangleIcon } from "../../../../icons";
import { TransactionRuleActions } from "../../../transactionRules/TransactionRuleActions";
import { TransactionRuleConditions } from "../../../transactionRules/TransactionRuleConditions";
import { Counterparty } from "../../../../common/types/counterparty";
import { TransactionRuleGroup } from "../../../../common/types/transactionRule";
import styles from "../RulesTable.module.scss";
import { Logo } from "../../../general/Logo";

interface TitleSectionProps {
    rulesCount: number;
    isCollapsed: boolean;
    onToggleCollapse: () => void;
}

const TitleSection: React.FC<TitleSectionProps> = ({
    rulesCount: _rulesCount,
    isCollapsed: _isCollapsed,
    onToggleCollapse,
}) => (
    <>
        <div className="d-flex justify-content-between">
            <div className="d-flex gap-1 align-items-center">
                <div className="d-flex gap-buttons align-items-center">
                    <Logo variant="icon" height={16} width={16} />
                    <span>Kick</span>
                </div>

                <Button
                    icon
                    size="sm"
                    variant="tertiary"
                    className={styles.visibleOnHover}
                    onClick={onToggleCollapse}
                >
                    <TriangleIcon />
                </Button>
            </div>

            <Button
                asChild
                icon
                size="sm"
                variant="tertiary"
                tooltip="The Kick Rule Group maps transactions to the Chart of Accounts based on Category and Entity Type. See Help Center to learn more."
            >
                <a
                    href="https://kickfinance.notion.site/Rules-1af64c4ef01480328c7df46e6d609e89"
                    target="_blank"
                >
                    <ArrowCornerIcon />
                </a>
            </Button>
        </div>
        <span className={styles.defaultRulesDescription}>
            Runs when no other Accounting Rule applies.
        </span>
    </>
);

interface AccountingRulesTableProps {
    accountingGroup: TransactionRuleGroup;
    counterparties: Counterparty[];
    priority: number;
}

export const AccountingRulesTable: React.FC<AccountingRulesTableProps> = ({
    accountingGroup,
    counterparties,
    priority,
}) => {
    const [isCollapsed, setIsCollapsed] = useState(true);

    return (
        <>
            <thead></thead>
            <tbody>
                {!accountingGroup.rules?.length ? (
                    <tr>
                        <td className={styles.td} width={50}>
                            {priority}
                        </td>
                        <td
                            className={classNames(
                                styles.td,
                                styles.groupHeader,
                            )}
                            width={200}
                        >
                            <TitleSection
                                rulesCount={0}
                                isCollapsed={isCollapsed}
                                onToggleCollapse={() =>
                                    setIsCollapsed(!isCollapsed)
                                }
                            />
                        </td>
                        <td colSpan={3} className={styles.td}>
                            <span>No rules found</span>
                        </td>
                    </tr>
                ) : (
                    accountingGroup.rules?.map((rule, index) =>
                        !isCollapsed || index === 0 ? (
                            <tr key={rule.id}>
                                {index === 0 && (
                                    <>
                                        <td
                                            className={styles.td}
                                            rowSpan={
                                                accountingGroup.rules?.length
                                            }
                                            width={50}
                                        >
                                            {priority}
                                        </td>

                                        <td
                                            className={classNames(
                                                styles.td,
                                                styles.groupHeader,
                                            )}
                                            rowSpan={
                                                accountingGroup.rules?.length
                                            }
                                            width={200}
                                        >
                                            <TitleSection
                                                rulesCount={
                                                    accountingGroup.rules
                                                        ?.length ?? 0
                                                }
                                                isCollapsed={isCollapsed}
                                                onToggleCollapse={() =>
                                                    setIsCollapsed(!isCollapsed)
                                                }
                                            />
                                        </td>
                                    </>
                                )}
                                {isCollapsed ? (
                                    <td
                                        colSpan={3}
                                        className={classNames(
                                            styles.td,
                                            styles.rulesCount,
                                        )}
                                    >
                                        {accountingGroup.rules?.length ?? 0}{" "}
                                        rules
                                    </td>
                                ) : (
                                    <>
                                        <td className={styles.td}>
                                            <TransactionRuleConditions
                                                rule={rule}
                                                counterparties={counterparties}
                                            />
                                        </td>
                                        <td colSpan={3} className={styles.td}>
                                            <TransactionRuleActions
                                                rule={rule}
                                            />
                                        </td>
                                    </>
                                )}
                            </tr>
                        ) : null,
                    )
                )}
            </tbody>
        </>
    );
};
